/** @file The validateDoc problems a change adds: those of `after` that `before` does not account for. */
import type { Pose2DError } from '../pose2dError';

const INDEX = /^\d+$/;

/**
 * A problem's identity across a change: its code and its path with every array-index segment as `*` (F3). A repair
 * may change the values a message names, and an insert or delete before a broken element shifts its index.
 */
const shapeOf = (problem: Pose2DError): string => `${problem.code} ${(problem.path ?? '').split('.').map((seg) => (INDEX.test(seg) ? '*' : seg)).join('.')}`;

/**
 * The problems of the document after a change that the document before it did not already have, in order (R23-01).
 * Problems are matched by shape (code + path, any array index) as multisets: n old problems of one shape account for
 * at most n new ones, so adding a second broken element beside an old one is still fresh. A document that already
 * fails validateDoc stays repairable: a commit or a `project.replaceDoc` is refused only for what it adds. With no
 * `before` problems this is `after` itself, so a healthy document is judged exactly as by validateDoc.
 */
export function freshProblems(before: readonly Pose2DError[], after: readonly Pose2DError[]): Pose2DError[] {
  if (before.length === 0) return [...after];
  const left = new Map<string, number>();
  for (const problem of before) left.set(shapeOf(problem), (left.get(shapeOf(problem)) ?? 0) + 1);
  return after.filter((problem) => {
    const shape = shapeOf(problem);
    const count = left.get(shape) ?? 0;
    if (count === 0) return true;
    left.set(shape, count - 1);
    return false;
  });
}
